import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Gift, Sparkles } from "lucide-react";
import { getPromoConfig, freeMonthsOf, planCta, planPromoCaption, confirmActivate, publicPriceLabel } from "@/lib/promo";
import { useSeo } from "@/lib/seo";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";

export default function Pricing() {
    useSeo({
        title: "Prezzi e abbonamenti: Privati e Agenzie | VendiDirect",
        description: "Scegli il piano VendiDirect: Privato da 9,99€/mese per vendere casa in autonomia, Agenzia Pro e Agenzia Full con VD Manager CRM, feed portali e firma digitale. Nessun vincolo.",
        canonical: "/prezzi",
    });
    const [plans, setPlans] = useState([]);
    const [promoCfg, setPromoCfg] = useState({ active: false, per_plan_months: {}, eligible: [], max_months: 0, any_promo: false });
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activating, setActivating] = useState(null);
    const { user, refresh } = useAuth();
    const navigate = useNavigate();

    const registrationPath = (planId, mode) => {
        const accountType = planId === "base" ? "privato" : "agenzia";
        return `/registrazione?account_type=${accountType}&plan=${encodeURIComponent(planId)}&mode=${mode}`;
    };

    useEffect(() => {
        api.get("/subscription/plans").then((r) => setPlans(r.data.plans || []));
        getPromoConfig(true).then(setPromoCfg);
        if (user) {
            api.get("/subscription/me").then((r) => setSubscription(r.data)).catch(() => setSubscription(null));
        }
    }, [user]);

    const subscribe = async (plan_id) => {
        if (!user) { navigate(registrationPath(plan_id, "checkout")); return; }
        setLoading(true);
        try {
            const origin_url = PUBLIC_SITE_URL;
            const { data } = await api.post("/subscription/checkout", { plan_id, origin_url });
            window.location.href = data.url;
        } catch (e) {
            alert("Errore durante la creazione del checkout: " + (e.response?.data?.detail || e.message));
            setLoading(false);
        }
    };

    const activatePromoPlan = async (plan_id, planName) => {
        if (!user) { navigate(registrationPath(plan_id, "promo")); return; }
        // Channel-aware pre-check: main-plan promo and analytics_pro promo are INDEPENDENT.
        const isAddon = plans.find((p) => p.id === plan_id)?.addon;
        if (isAddon) {
            if (subscription?.analytics_pro?.promo_used) {
                alert("Promo VD Manager già utilizzata. Procedi con l'abbonamento standard.");
                return;
            }
        } else if (user.promo_used) {
            alert("Promo già utilizzata sul piano principale. Procedi con l'abbonamento standard.");
            return;
        }
        if (!window.confirm(confirmActivate(promoCfg, plan_id, planName))) return;
        setActivating(plan_id);
        try {
            await api.post("/subscription/activate-promo", { plan_id });
            await refresh();
            navigate("/dashboard");
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore attivazione promo");
        } finally { setActivating(null); }
    };

    const standardPlans = plans.filter((p) => !p.addon);
    const addonPlans = plans.filter((p) => p.addon);
    const showGlobalBanner = promoCfg.any_promo && !user?.promo_used;

    return (
        <div data-testid="pricing-page" className="pt-32 pb-24">
            <div className="w-full px-6 md:px-12 lg:px-20 2xl:px-28">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="label-eyebrow">Abbonamenti</span>
                    <h1 className="font-serif text-5xl sm:text-6xl text-ink-50 mt-4 leading-tight">Scegli il piano <span className="italic text-gold">su misura</span></h1>
                    <p className="text-ink-400 mt-6 leading-relaxed">Tre formule per privati e agenzie. Cancella quando vuoi. Senza vincoli, senza sorprese.</p>
                </div>

                {showGlobalBanner && (
                    <div data-testid="pricing-promo-banner" className="max-w-4xl mx-auto mb-14 border border-gold/50 bg-gradient-to-r from-gold/10 via-gold/5 to-transparent p-6 flex items-start gap-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-gold text-black flex-shrink-0"><Gift size={22} /></div>
                        <div className="flex-1">
                            <div className="font-serif text-xl text-gold">Promo lancio · fino a {promoCfg.max_months} mes{promoCfg.max_months === 1 ? "e" : "i"} grat{promoCfg.max_months === 1 ? "is" : "is"}</div>
                            <p className="text-sm text-ink-100 mt-2">
                                Alcuni piani sono <strong className="text-gold">gratuiti in fase di lancio</strong>. Attivazione immediata senza carta di credito.
                            </p>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {standardPlans.map((p) => {
                        const featured = p.id === "premium";
                        const paymentRequired = subscription?.plan_id === p.id && subscription?.payment_required;
                        const isCurrent = subscription?.plan_id === p.id && subscription?.plan_active && !paymentRequired;
                        const planFreeMonths = freeMonthsOf(promoCfg, p.id);
                        const planHasPromo = planFreeMonths > 0 && promoCfg.active && !user?.promo_used;
                        return (
                            <div
                                key={p.id}
                                data-testid={`plan-${p.id}`}
                                className={`relative p-10 border ${featured ? "border-gold bg-gradient-to-b from-ink-900 to-ink-950" : "border-white/10 bg-ink-900/40"} flex flex-col`}
                            >
                                {featured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-black text-[10px] tracking-[0.3em] uppercase px-4 py-1.5 font-bold shadow-md">Più scelto</div>
                                )}
                                {p.id === "agenzia" && <Crown size={20} className="text-gold mb-4" />}
                                <h3 className="font-serif text-3xl text-ink-50">{p.name}</h3>
                                <div className="mt-6">
                                    {planHasPromo ? (
                                        <>
                                            <div className="flex items-baseline gap-3">
                                                <span className="font-serif text-5xl text-gold">Gratis</span>
                                                {Number(p.price) > 0 && <span className="text-xs text-ink-400 line-through">{publicPriceLabel(p.price)}/mese</span>}
                                            </div>
                                            <div data-testid={`plan-promo-caption-${p.id}`} className="text-xs text-gold mt-1">
                                                {planPromoCaption(promoCfg, p.id, p.price)}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-serif text-5xl text-price">{publicPriceLabel(p.price, !!p.included_in_plan)}</span>
                                            {Number(p.price) > 0 && <span className="text-ink-400 text-sm ml-2">/ mese</span>}
                                        </>
                                    )}
                                </div>
                                <ul className="mt-10 space-y-4 flex-1">
                                    {p.features.map((f, i) => (
                                        <PlanFeature key={i} text={f} testid={`plan-feature-${p.id}-${i}`} />
                                    ))}
                                </ul>
                                {isCurrent ? (
                                    <div className="mt-10 py-4 text-center text-sm bg-green-900/20 border border-green-700/40 text-green-300">Piano attuale</div>
                                ) : planHasPromo ? (
                                    <button
                                        onClick={() => activatePromoPlan(p.id, p.name)}
                                        disabled={activating === p.id}
                                        data-testid={`activate-promo-${p.id}`}
                                        className={`mt-10 py-4 text-sm transition-colors ${featured ? "cta-gold cta-gold-pulse font-medium" : "cta-outline font-medium"}`}
                                    >
                                        {planCta(promoCfg, p.id, activating === p.id)}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => subscribe(p.id)}
                                        disabled={loading}
                                        data-testid={`subscribe-${p.id}`}
                                        className={`mt-10 py-4 text-sm transition-colors ${featured ? "cta-gold cta-gold-pulse font-medium" : "cta-outline font-medium"}`}
                                    >
                                        {loading ? "Attendere..." : paymentRequired ? "Completa il pagamento →" : "Abbonati ora →"}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Analytics Pro (add-on agenzie) */}
                {addonPlans.length > 0 && (
                    <div className="mt-20" data-testid="addon-section">
                        <div className="text-center max-w-2xl mx-auto mb-10">
                            <span className="label-eyebrow text-purple-300">Add-on Agenzie · a parte</span>
                            <h2 className="font-serif text-4xl text-ink-50 mt-3">Analytics <span className="italic text-purple-300">Pro</span></h2>
                            <p className="text-ink-400 mt-4 text-sm">Costo aggiuntivo rispetto al piano Agenzia. Sblocca: dashboard KPI avanzata, soglie personalizzabili, distribuzione feed portali esterni (Idealista, Casa.it, Immobiliare.it), report mensili.</p>
                        </div>
                        <div className="max-w-2xl mx-auto">
                            {addonPlans.map((p) => {
                                const apActive = !!subscription?.analytics_pro?.active;
                                const needsAgency = !user || !subscription || !!subscription?.analytics_pro?.requires_agency_plan;
                                const canPurchase = !!subscription?.analytics_pro?.can_purchase;
                                const apPromoUsed = !!subscription?.analytics_pro?.promo_used;
                                const introUsed = user?.[`intro_used_${p.id}`];
                                const apFreeMonths = freeMonthsOf(promoCfg, p.id);
                                const apHasPromo = apFreeMonths > 0 && promoCfg.active && !apPromoUsed && !needsAgency && !apActive;
                                return (
                                    <div key={p.id} data-testid={`plan-${p.id}`} className="border border-purple-700/40 bg-gradient-to-br from-purple-900/20 to-ink-950 p-10 relative overflow-hidden">
                                        <Sparkles size={28} className="text-purple-300 mb-4" />
                                        <h3 className="font-serif text-3xl text-ink-50">{p.name}</h3>
                                        <div className="mt-2 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-purple-200 border border-purple-700/40 bg-purple-900/30 px-2 py-1">
                                            Add-on · si somma al piano Agenzia
                                        </div>
                                        <div className="mt-6 flex items-baseline gap-3">
                                            {apHasPromo ? (
                                                <>
                                                    <span className="font-serif text-5xl text-purple-200">Gratis</span>
                                                    {Number(p.price) > 0 && <span className="text-xs text-purple-200/70 line-through">{publicPriceLabel(p.price)}/mese</span>}
                                                </>
                                            ) : p.intro_price && !introUsed ? (
                                                <>
                                                    <span className="font-serif text-5xl text-purple-200">{publicPriceLabel(p.intro_price, !!p.included_in_plan)}</span>
                                                    {Number(p.intro_price) > 0 && <span className="text-ink-400 text-sm">/ 1° mese</span>}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="font-serif text-5xl text-purple-200">{publicPriceLabel(p.price, !!p.included_in_plan)}</span>
                                                    {Number(p.price) > 0 && <span className="text-ink-400 text-sm">/ mese</span>}
                                                </>
                                            )}
                                        </div>
                                        {apHasPromo && (
                                            <div data-testid="analytics-pro-promo-caption" className="text-xs text-purple-200 mt-1">
                                                {planPromoCaption(promoCfg, p.id, p.price)}
                                            </div>
                                        )}
                                        {!apHasPromo && p.intro_price && !introUsed && (
                                            <div className="text-xs text-purple-200/70 mt-1">{Number(p.price) > 0 ? `poi ${publicPriceLabel(p.price)}/mese (rinnovo)` : (p.included_in_plan ? "Incluso nel piano base" : "Extra gratuito")}</div>
                                        )}
                                        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {p.features.map((f, i) => (
                                                <PlanFeature key={i} text={f} testid={`plan-feature-${p.id}-${i}`} accent="purple" />
                                            ))}
                                        </ul>
                                        {apActive ? (
                                            <div data-testid="analytics-pro-active" className="mt-8 py-4 text-center text-sm bg-green-900/20 border border-green-700/40 text-green-300">
                                                {subscription?.analytics_pro?.admin_bypass
                                                    ? "👑 Accesso admin illimitato"
                                                    : `✓ Add-on attivo${subscription?.analytics_pro?.days_left != null ? ` — ${subscription.analytics_pro.days_left} giorni residui` : ""}`}
                                            </div>
                                        ) : needsAgency ? (
                                            <>
                                                <button
                                                    disabled
                                                    data-testid={`subscribe-${p.id}-disabled`}
                                                    className="mt-8 w-full py-4 text-sm bg-purple-800/40 text-purple-200/60 cursor-not-allowed font-medium"
                                                >
                                                    Richiede piano Agenzia attivo
                                                </button>
                                                <p data-testid="analytics-pro-need-agency" className="mt-3 text-xs text-amber-300/90">
                                                    Devi prima attivare il piano <strong>Agenzia</strong> (€79,90/mese) per poter sottoscrivere questo add-on.
                                                </p>
                                            </>
                                        ) : apHasPromo ? (
                                            <button
                                                onClick={() => activatePromoPlan(p.id, p.name)}
                                                disabled={activating === p.id}
                                                data-testid={`activate-promo-${p.id}`}
                                                className="mt-8 w-full py-4 text-sm bg-purple-600 hover:bg-purple-500 text-white transition-colors font-medium disabled:opacity-40"
                                            >
                                                {planCta(promoCfg, p.id, activating === p.id)}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => subscribe(p.id)}
                                                disabled={loading || !canPurchase}
                                                data-testid={`subscribe-${p.id}`}
                                                className="mt-8 w-full py-4 text-sm bg-purple-600 hover:bg-purple-500 text-white transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                {loading ? "Attendere..." : (p.intro_price && !introUsed ? `Attiva — 1° mese ${publicPriceLabel(p.intro_price, !!p.included_in_plan)}` : "Attiva VD Manager")}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <ComparisonTable />
                    </div>
                )}

                <p className="text-center text-xs text-ink-400 mt-12">
                    Pagamenti sicuri tramite Stripe. Carte di credito/debito accettate. IVA inclusa.
                </p>
            </div>
        </div>
    );
}

const splitFeature = (text) => {
    const value = String(text || "");
    const colon = value.indexOf(":");
    if (colon > 0) return [value.slice(0, colon), value.slice(colon + 1).trim()];
    if (value.startsWith("Annunci illimitati con ")) return ["Annunci illimitati", value.slice("Annunci illimitati ".length)];
    if (value.startsWith("Profilo agenzia con ")) return ["Profilo agenzia", value.slice("Profilo agenzia ".length)];
    if (value.includes(" + ")) {
        const [title, ...rest] = value.split(" + ");
        return [title, `+ ${rest.join(" + ")}`];
    }
    if (value.startsWith("Analytics avanzate, ")) return ["Analytics avanzate", value.slice("Analytics avanzate".length)];
    return [value, ""];
};

const PlanFeature = ({ text, testid, accent = "gold" }) => {
    const [title, description] = splitFeature(text);
    return (
        <li data-testid={testid} className="flex items-start gap-3 text-sm text-ink-100">
            <Check size={16} className={`${accent === "purple" ? "text-purple-300" : "text-gold"} mt-0.5 shrink-0`} />
            <span className="min-w-0">
                <span className="font-medium text-ink-50">{title}</span>
                {description && <span className="block text-xs text-ink-400 leading-relaxed mt-0.5">{description}</span>}
            </span>
        </li>
    );
};

const COMPARE_ROWS = [
    ["Gestione annunci (pubblica, modifica, stato)", true, true],
    ["Views e contatti totali", true, true],
    ["Andamento contatti ultimi 30 giorni", true, true],
    ["Views e contatti per singolo annuncio", true, true],
    ["KPI di business: conversion rate e accept rate", false, true],
    ["Media views per annuncio e ripartizione portafoglio", false, true],
    ["Classifica performance fino a 50 annunci", false, true],
    ["Alert automatici con soglie personalizzate", false, true],
    ["Feed multi-portale (Idealista, Casa.it) con validatore", false, true],
    ["Statistiche accessi feed esterni", false, true],
];

function ComparisonTable() {
    return (
        <div data-testid="dashboard-vs-pro-table" className="max-w-3xl mx-auto mt-14">
            <h3 className="font-serif text-2xl text-ink-50 text-center mb-6">Dashboard standard <span className="text-ink-400">vs</span> <span className="text-purple-300">VD Manager</span></h3>
            <div className="border border-white/10 overflow-hidden">
                <div className="grid grid-cols-12 text-[11px] uppercase tracking-wider text-ink-400 bg-white/5 px-4 py-3">
                    <div className="col-span-6">Funzionalità</div>
                    <div className="col-span-3 text-center">Dashboard</div>
                    <div className="col-span-3 text-center text-purple-300">VD Manager</div>
                </div>
                {COMPARE_ROWS.map(([label, std, pro], i) => (
                    <div key={i} className="grid grid-cols-12 items-center px-4 py-3 text-sm text-ink-100 border-t border-white/5">
                        <div className="col-span-6">{label}</div>
                        <div className="col-span-3 text-center">{std ? <Check size={15} className="inline text-gold" /> : <span className="text-ink-500">—</span>}</div>
                        <div className="col-span-3 text-center">{pro ? <Check size={15} className="inline text-purple-300" /> : <span className="text-ink-500">—</span>}</div>
                    </div>
                ))}
            </div>
            <p className="text-[11px] text-ink-400 mt-3 text-center">Il feed multi-portale genera l'URL/XML da fornire ai portali: la pubblicazione effettiva dipende dall'accordo dell'agenzia con il portale.</p>
        </div>
    );
}
